import React from 'react';
import { getDomainConfig, CONTACT_INFO } from '../config';
import './LegalPage.css';

function PrivacyPage() {
  const config = getDomainConfig();
  const hostname = window.location.hostname.replace('www.', '');
  const displayEmail = `info@${hostname}`;

  return (
    <div className="legal-page">
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last Updated: February 2026</p>

      <div className="legal-content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            {config.name}, operated by {CONTACT_INFO.businessName} ("we," "our," or "us"), is committed
            to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you visit our website {hostname} and use our services.
          </p>
          <p>
            Please read this Privacy Policy carefully. By accessing or using our website, you acknowledge
            that you have read, understood, and agree to be bound by this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <h3>Personal Information You Provide</h3>
          <ul>
            <li>Name, email address, and phone number</li>
            <li>Company name and business information</li>
            <li>Mailing and billing addresses</li>
            <li>Messages you send through our contact forms</li>
            <li>Product inquiries and quote requests</li>
          </ul>
          <h3>Information Collected Automatically</h3>
          <ul>
            <li>IP address and browser type</li>
            <li>Device information and operating system</li>
            <li>Pages visited, time spent, and navigation patterns</li>
            <li>Referring website addresses</li>
            <li>Cookies and similar tracking technologies (see Section 5)</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Process product quote requests and connect you with suppliers</li>
            <li>Send you information about products and services you have requested</li>
            <li>Improve our website, products, and services</li>
            <li>Analyze website usage to enhance user experience</li>
            <li>Prevent fraud and ensure the security of our website</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share
            your information in the following circumstances:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> We may share information with third-party vendors
            who perform services on our behalf, such as website hosting, analytics, and email delivery.</li>
            <li><strong>Suppliers:</strong> When you request a product quote, we may share your contact
            information with relevant suppliers to fulfill your request.</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required to do so by
            law or in response to valid requests by public authorities.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of
            assets, your information may be transferred as part of that transaction.</li>
          </ul>
        </section>

        <section>
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to collect and track information about
            your browsing activity on our website. These technologies help us:
          </p>
          <ul>
            <li>Remember your preferences and settings</li>
            <li>Understand how you use our website</li>
            <li>Measure the effectiveness of our content</li>
            <li>Deliver relevant content and improve functionality</li>
          </ul>
          <p>
            We use Google Analytics and Google Tag Manager to analyze website traffic and usage patterns.
            Google Analytics uses cookies to collect information about your use of our website. This
            information is transmitted to and stored by Google. For more information on how Google uses
            data, please visit Google's Privacy Policy.
          </p>
          <p>
            You can control cookies through your browser settings. Most browsers allow you to refuse
            cookies or alert you when cookies are being sent. Please note that disabling cookies may
            affect the functionality of our website.
          </p>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your
            personal information against unauthorized access, alteration, disclosure, or destruction.
            Our website uses SSL/TLS encryption to secure data transmitted between your browser and
            our servers.
          </p>
          <p>
            While we strive to protect your personal information, no method of transmission over the
            Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>Depending on your location, you may have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate personal information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time</li>
            <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided below.
            We will respond to your request within 30 days.
          </p>
        </section>

        <section>
          <h2>8. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, the California Consumer Privacy Act (CCPA) provides you
            with additional rights regarding your personal information, including the right to know what
            personal information is collected, the right to delete personal information, and the right
            to opt out of the sale of personal information. As stated above, we do not sell your
            personal information.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our website is not intended for children under the age of 13. We do not knowingly collect
            personal information from children under 13. If we become aware that we have collected
            information from a child under 13, we will take steps to delete such information.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last Updated" date. We
            encourage you to review this Privacy Policy periodically.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or our data practices,
            please contact us:
          </p>
          <p><strong>{CONTACT_INFO.businessName}</strong></p>
          <p>Email: {displayEmail}</p>
          <p>Phone: {CONTACT_INFO.phone}</p>
          <p>Address: {CONTACT_INFO.address}, {CONTACT_INFO.city}, {CONTACT_INFO.state} {CONTACT_INFO.zip}</p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPage;
